import { ADMIN_AUCTIONS } from './mockAdapter';

export type AuctionStatus = 'active' | 'ending_soon' | 'settled';

export interface AuctionTimelineEntry {
  description: string;
  timestamp: string;
  bidder?: string;
  amount?: number;
  txHash?: string;
}

export interface AuctionDetail {
  id: string;
  facilityName: string;
  tokenName: string;
  tokenSymbol: string;
  status: AuctionStatus;
  collateralAmount: number;
  collateralValue: number;
  debtAmount: number;
  reservePrice: number;
  currentBid: number;
  bidCount: number;
  timeRemaining: string;
  settlementToken: string;
  timeline: AuctionTimelineEntry[];
}

function statusFromAdmin(status: string): AuctionStatus {
  if (status === 'ending_soon') return 'ending_soon';
  if (status === 'settled') return 'settled';
  return 'active';
}

function timelineFromAuction(auction: (typeof ADMIN_AUCTIONS)[number]): AuctionTimelineEntry[] {
  const timeline: AuctionTimelineEntry[] = [
    {
      description: 'Auction created following liquidation trigger',
      timestamp: '22/03/2026, 19:56:05',
      txHash: `0x${auction.id.replace(/[^0-9]/g, '').slice(0, 6).padStart(6, '0')}...001`,
    },
  ];

  if (auction.bidCount <= 0) {
    return timeline;
  }

  const running = auction.reservePrice.replace(/[^0-9]/g, '');
  const runningNum = Number(running);
  for (let i = 0; i < auction.bidCount; i++) {
    const amount = Math.round(runningNum * (1 + (i + 1) * 0.01));
    timeline.push({
      description: i === auction.bidCount - 1
        ? `Current leading bid at $${amount.toLocaleString()}`
        : i === 0
          ? `First bid placed at $${amount.toLocaleString()}`
          : `Bid increased to $${amount.toLocaleString()}`,
      timestamp: `22/03/2026, ${String((20 + i) % 24).padStart(2, '0')}:${String((i * 11) % 60).padStart(2, '0')}:00`,
      bidder: `0xBidder${i + 1}...`,
      amount,
      txHash: `0xbid${String(i + 1).padStart(3, '0')}...${String((i + 10) * 17).padStart(3, '0')}`,
    });
  }
  return timeline;
}

export function generateAuctionDetail(auctionId: string, facilityName: string): AuctionDetail {
  const auction = ADMIN_AUCTIONS.find((item) => item.id === auctionId);

  if (!auction) {
    return {
      id: auctionId,
      facilityName,
      tokenName: 'Unknown Token',
      tokenSymbol: 'UNK',
      status: 'active',
      collateralAmount: 0,
      collateralValue: 0,
      debtAmount: 0,
      reservePrice: 0,
      currentBid: 0,
      bidCount: 0,
      timeRemaining: '-',
      settlementToken: 'USDC',
      timeline: [
        {
          description: 'Auction not found in mocked data source',
          timestamp: '22/03/2026, 19:56:05',
        },
      ],
    };
  }

  return {
    id: auction.id,
    facilityName: auction.facilityName || facilityName,
    tokenName: auction.tokenName,
    tokenSymbol: auction.tokenSymbol,
    status: statusFromAdmin(auction.status),
    collateralAmount: auction.collateralAmount,
    collateralValue: auction.collateralValue,
    debtAmount: auction.debtAmount,
    reservePrice: Number(auction.reservePrice.replace(/[^0-9]/g, '')),
    currentBid: Number(auction.currentBid.replace(/[^0-9]/g, '')),
    bidCount: auction.bidCount,
    timeRemaining: auction.timeRemaining,
    settlementToken: auction.settlementToken,
    timeline: timelineFromAuction(auction),
  };
}

export const AUCTION_STATUS_CLASS: Record<AuctionStatus, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-700' },
  ending_soon: { label: 'Ending Soon', className: 'bg-yellow-100 text-yellow-700' },
  settled: { label: 'Settled', className: 'bg-gray-100 text-gray-600' },
};
